/**
 * In-progress object edits that may need to be flushed onto a page manifest.
 * Mirrors the objectsState fields saveLesson reads when it rescues the currently
 * displayed page.
 */
export interface MirrorableObjects {
  objectList?: any[];
  animatedObjects?: any[];
  images?: any[];
  textBoxes?: any[];
  tables?: any[];
  annotations?: any[];
  videos?: any[];
  hotspots?: any[];
}

/** The freeForm-only fields, gated exactly as saveLesson gates them. */
function mirrorFreeFormFields(pm: any, objects: MirrorableObjects): void {
  pm.timeline = {
    animatedObjects: objects.animatedObjects,
    sequenceLength: pm?.timeline?.sequenceLength ?? 30,
  };
  pm.video = objects.videos?.[0];
  pm.pageImage = objects.images;
  pm.textBlock = objects.textBoxes;

  const scorm = objects.objectList?.find((object: any) => object?.type === "SCORM");
  if (scorm) {
    pm.pageScormObject = [scorm];
  }
  if (objects.tables) {
    pm.tables = objects.tables;
  }
  if (objects.hotspots) {
    pm.hotspots = objects.hotspots;
  }
}

/**
 * Writes the displayed page's in-progress object edits onto its page manifest,
 * in place.
 *
 * The field set and the freeForm gating deliberately mirror saveLesson's
 * "if we save the current page has not had the chance to save in the
 * lessonPagesContext" block (builder.tsx). That block is the existing contract
 * for turning objectsState back into a manifest, and it runs against the same
 * builder-owned `lessonPages` array this helper writes to — the array that
 * saveLesson serialises. Matching it means flushing on navigation can only ever
 * produce a manifest saveLesson would have produced anyway on that page.
 *
 * Do NOT widen this to the SET_OBJECTS_ON_PAGE_MANIFEST reducer's ungated field
 * set. That reducer writes the LessonPagesProvider copy, which saveLesson
 * filters by page type before persisting; this helper has no such filter in
 * front of it, so ungated writes would reach the server (ticket 25649 review).
 */
export function mirrorObjectsOntoPageManifest(pm: any, objects: MirrorableObjects, isFreeFormBasePage: boolean): void {
  if (!pm) return;
  if (isFreeFormBasePage) {
    mirrorFreeFormFields(pm, objects);
  }
  pm.annotations = objects.annotations;
}
